import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { UserPlus, Search, Link, Mail } from "lucide-react";
import { useCreateStaffInvitation, useLinkExistingUserToStaff, useSearchUserByEmail } from "@/hooks/use-staff-invitations";
import { useInvitationPermissions } from "@/hooks/use-invitation-permissions";
import { DeviceMultiSelect } from "@/components/common/DeviceMultiSelect";
import { toast } from "sonner";

interface StaffInviteModalProps {
  staffId: string;
  staffEmail: string;
  staffName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const StaffInviteModal = ({ staffId, staffEmail, staffName, open, onOpenChange }: StaffInviteModalProps) => {
  const [inviteForm, setInviteForm] = useState({
    email: staffEmail,
    role: 'admin' as 'admin' | 'superuser' | 'internal_staff' | 'advisor' | 'production',
    firstName: '',
    lastName: ''
  });
  const [selectedDeviceIds, setSelectedDeviceIds] = useState<string[]>([]);
  const [searchEmail, setSearchEmail] = useState(staffEmail);
  const [linkRole, setLinkRole] = useState<'admin' | 'superuser'>('admin');

  const { data: permissions } = useInvitationPermissions();
  const createInvitation = useCreateStaffInvitation();
  const linkUser = useLinkExistingUserToStaff();
  const searchUser = useSearchUserByEmail();

  if (!permissions?.canSendInvitations) {
    return null;
  }

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!inviteForm.email) {
      toast.error("Email is required");
      return;
    }

    try {
      await createInvitation.mutateAsync({
        email: inviteForm.email,
        staffId,
        role: inviteForm.role,
        organizationId: permissions.organizationId || undefined,
        firstName: inviteForm.firstName || undefined,
        lastName: inviteForm.lastName || undefined,
        deviceIds: selectedDeviceIds
      });
      onOpenChange(false);
      setSelectedDeviceIds([]);
    } catch (error) {
      console.error("Error sending invitation:", error);
    }
  };

  const handleSearch = async () => {
    if (!searchEmail) {
      toast.error("Email is required");
      return;
    }

    try {
      await searchUser.mutateAsync({ email: searchEmail });
    } catch (error) {
      console.error("Error searching user:", error);
    }
  };

  const handleLink = async () => {
    if (!searchUser.data?.found || !searchUser.data.user) {
      toast.error("No user found to link");
      return;
    }

    try {
      await linkUser.mutateAsync({
        staffId,
        userId: searchUser.data.user.id,
        role: linkRole,
        organizationId: permissions.organizationId || undefined
      });
      onOpenChange(false);
    } catch (error) {
      console.error("Error linking user:", error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5" />
            Platform Access for {staffName}
          </DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="invite" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="invite" className="flex items-center gap-2">
              <Mail className="h-4 w-4" />
              Send Invitation
            </TabsTrigger>
            <TabsTrigger value="link" className="flex items-center gap-2">
              <Link className="h-4 w-4" />
              Link Existing User
            </TabsTrigger>
          </TabsList>

          <TabsContent value="invite" className="space-y-4">
            <div className="text-sm text-muted-foreground">
              Send an invitation email to give this staff member access to the platform.
            </div>
            
            <form onSubmit={handleInvite} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName">First Name (Optional)</Label>
                  <Input
                    id="firstName"
                    value={inviteForm.firstName}
                    onChange={(e) => setInviteForm({ ...inviteForm, firstName: e.target.value })}
                    placeholder="Enter first name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName">Last Name (Optional)</Label>
                  <Input
                    id="lastName"
                    value={inviteForm.lastName}
                    onChange={(e) => setInviteForm({ ...inviteForm, lastName: e.target.value })}
                    placeholder="Enter last name"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email Address *</Label>
                <Input
                  id="email"
                  type="email"
                  required
                  value={inviteForm.email}
                  onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                  placeholder="Enter email address"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="role">Platform Role *</Label>
                <Select value={inviteForm.role} onValueChange={(value: 'admin' | 'superuser' | 'internal_staff' | 'advisor' | 'production') => setInviteForm({ ...inviteForm, role: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="superuser">Superuser</SelectItem>
                    <SelectItem value="internal_staff">Internal Staff</SelectItem>
                    <SelectItem value="advisor">Advisor</SelectItem>
                    <SelectItem value="production">Production</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <DeviceMultiSelect
                organizationId={permissions?.organizationId}
                selectedDeviceIds={selectedDeviceIds}
                onSelectionChange={setSelectedDeviceIds}
                disabled={createInvitation.isPending}
              />

              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={createInvitation.isPending}>
                  {createInvitation.isPending ? "Sending..." : "Send Invitation"}
                </Button>
              </div>
            </form>
          </TabsContent>

          <TabsContent value="link" className="space-y-4">
            <div className="text-sm text-muted-foreground">
              Search for an existing user account to link to this staff member.
            </div>

            <div className="space-y-4">
              <div className="flex gap-2">
                <div className="flex-1">
                  <Label htmlFor="searchEmail">Email Address</Label>
                  <Input
                    id="searchEmail"
                    type="email"
                    value={searchEmail}
                    onChange={(e) => setSearchEmail(e.target.value)}
                    placeholder="Enter email to search"
                  />
                </div>
                <div className="flex items-end">
                  <Button 
                    onClick={handleSearch} 
                    disabled={searchUser.isPending}
                    variant="outline"
                  >
                    <Search className="h-4 w-4 mr-2" />
                    {searchUser.isPending ? "Searching..." : "Search"}
                  </Button>
                </div>
              </div>

              {searchUser.data && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm">Search Results</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {searchUser.data.found ? (
                      <div className="space-y-4">
                        <div className="flex items-center justify-between p-4 border rounded-lg">
                          <div>
                            <div className="font-medium">
                              {searchUser.data.user?.first_name} {searchUser.data.user?.last_name}
                            </div>
                            <div className="text-sm text-muted-foreground">
                              {searchUser.data.user?.email}
                            </div>
                            <div className="text-sm text-muted-foreground">
                              @{searchUser.data.user?.username}
                            </div>
                            {searchUser.data.user?.current_role && (
                              <Badge variant="outline" className="mt-1">
                                Current Role: {searchUser.data.user.current_role}
                              </Badge>
                            )}
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label>Platform Role</Label>
                          <Select value={linkRole} onValueChange={(value: 'admin' | 'superuser') => setLinkRole(value)}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="admin">Admin</SelectItem>
                              <SelectItem value="superuser">Superuser</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="flex justify-end gap-3">
                          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                            Cancel
                          </Button>
                          <Button onClick={handleLink} disabled={linkUser.isPending}>
                            {linkUser.isPending ? "Linking..." : "Link User"}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-4 text-muted-foreground">
                        No user found with email: {searchEmail}
                        <div className="mt-2 text-sm">
                          You can send them an invitation instead.
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};